// helpers
// --- helpers ---
import { countryName, toA2 } from "@/features/landing/utils/country-name";

const toNum = (v) => (
  typeof v === 'number' ? v
  : typeof v === 'string'
    ? Number(v.replace(/[^\d,.-]/g, '').replace(',', '.'))
    : NaN
);

const pickFirstDefined = (...vals) => {
  for (const v of vals) {
    const n = toNum(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
};


const getCurrency = (p = {}) => {
  const c =
    p.priceCurrency ||
    p.price?.currency ||
    p.raw?.price?.currency ||
    p.send?.currency ||
    p.sendCurrency ||
    p.faceCurrency ||
    p.currency ||
    p.raw?.currency ||
    'USD';
  return typeof c === 'string' ? c.toUpperCase() : 'USD';
};


// Keep for backwards-compat (single value path)
// eslint-disable-next-line no-unused-vars
const getMinorAndDivisor = (p = {}) => {
  const divisor = Math.max(
    1,
    toNum(
      p.priceCurrencyDivisor ??
      p.price?.currencyDivisor ??
      p.raw?.price?.currencyDivisor ??
      100
    ) || 1
  );
  const minor = pickFirstDefined(
    p.price?.suggestedFixed,
    p.price?.fixed,
    p.price?.min,
    p.raw?.price?.suggestedFixed,
    p.raw?.price?.fixed,
    p.raw?.price?.min,
    typeof p.price === 'number' ? p.price : null
  );
  return { minor, divisor };
};

// New: explicit range (min/max) without collapsing
const getMinorDivisorRange = (p = {}) => {
  const divisor = Math.max(
    1,
    toNum(
      p.priceCurrencyDivisor ??
      p.price?.currencyDivisor ??
      p.raw?.price?.currencyDivisor ??
      100
    ) || 1
  );

  const minMinor = pickFirstDefined(p.price?.min, p.raw?.price?.min);
  const maxMinor = pickFirstDefined(p.price?.max, p.raw?.price?.max);

  // also keep the legacy "minor" (suggested/fixed/min) so old UI still works
  const minor = pickFirstDefined(
    p.price?.suggestedFixed,
    p.price?.fixed,
    p.price?.min,
    p.raw?.price?.suggestedFixed,
    p.raw?.price?.fixed,
    p.raw?.price?.min,
    typeof p.price === 'number' ? p.price : null
  );

  return { minor, minMinor, maxMinor, divisor };
};

const convert = (amount, from, to, fx) => {
  if (!Number.isFinite(amount) || !from || !to || from === to) return amount;
  const direct = fx?.[from]?.[to];
  if (Number.isFinite(toNum(direct))) return amount * toNum(direct);
  const inverse = fx?.[to]?.[from];
  if (Number.isFinite(toNum(inverse))) return amount / toNum(inverse);
  const viaUSD = fx?.[from]?.USD && fx?.USD?.[to];
  if (Number.isFinite(toNum(viaUSD))) {
    return amount * toNum(fx[from].USD) * toNum(fx.USD[to]);
  }
  return null;
};

export function formatProduct(product, user, opts = {}) {
  const inferredType =
  product.productType ||
  product.type ||
  product.__source?.toUpperCase() ||
  (product.dataGB || product.durationDays ? "ESIM" : null) ||
  (product.send || product.topupSubtype || product.recipientGets ? "TOPUP" : null) ||
  "VOUCHER";

const type = String(inferredType).toUpperCase();
  const priceObj = product.price || product.raw?.price || {};
  const subTypes = product.subTypes || [];
  const lowerSubTypes = subTypes.map((s) => s.toLowerCase());
  const isMobileTopUp  = lowerSubTypes.includes('mobile top up');
  const isMobileData   = lowerSubTypes.includes('mobile data');
  const isMobileBundle = lowerSubTypes.includes('mobile bundle');

  let topupSubtype = null;
  if (isMobileData) topupSubtype = 'MOBILE DATA';
  else if (isMobileBundle) topupSubtype = 'BUNDLE';
  else if (isMobileTopUp) topupSubtype = 'TOP-UP';

  const details = [];
  if (toNum(product.durationDays) > 0) details.push({ label: 'Duration', value: `${product.durationDays} days`, icon: '📅' });
  if (product.dataUnlimited) details.push({ label: 'Data', value: 'Unlimited', icon: '🌐' });
  else if (toNum(product.dataGB) > 0) details.push({ label: 'Data', value: `${product.dataGB} GB`, icon: '🌐' });
  if (product.voiceUnlimited) details.push({ label: 'Voice', value: 'Unlimited', icon: '📞' });
  else if (toNum(product.voiceMinutes) > 0) details.push({ label: 'Voice', value: `${product.voiceMinutes} mins`, icon: '📞' });
  if (product.smsUnlimited) details.push({ label: 'SMS', value: 'Unlimited', icon: '💬' });
  else if (toNum(product.smsNumber) > 0) details.push({ label: 'SMS', value: `${product.smsNumber}`, icon: '💬' });

  // Regional multi-country bundles have no single country — list the actual
  // covered countries (e.g. "North America: Canada, United States, Mexico")
  // instead of just a count, so buyers know exactly where it works.
  if (!product.country) {
    const codes = Array.isArray(product.roamingCountries) ? product.roamingCountries : [];
    const region = Array.isArray(product.regions) && product.regions.length ? product.regions[0] : 'Regional';
    const names = codes.map((c) => countryName(toA2(c))).filter(Boolean);
    if (names.length) {
      const shown = names.slice(0, 8);
      const more = names.length - shown.length;
      details.push({
        label: 'Coverage',
        value: `${region}: ${shown.join(', ')}${more > 0 ? ` +${more} more` : ''}`,
        icon: '🧭',
      });
    }
  }

// ----- price normalization -----
const preNormalized = toNum(product.priceValue);
const { minor, minMinor, maxMinor, divisor } = getMinorDivisorRange(product);
const nativeCurrency = getCurrency(product);

const computedValue = Number.isFinite(minor) ? (minor / divisor) : null;
const priceValue = Number.isFinite(preNormalized) ? preNormalized : computedValue; // single value kept
const priceCurrency = nativeCurrency;

// range (native currency)
const priceMinValue = Number.isFinite(minMinor) ? (minMinor / divisor) : null;
const priceMaxValue = Number.isFinite(maxMinor) ? (maxMinor / divisor) : null;
const hasRange = Number.isFinite(priceMinValue) && Number.isFinite(priceMaxValue) && priceMaxValue >= priceMinValue;

const priceType = String(priceObj.type || priceObj.priceType || (hasRange ? 'RANGE' : 'FIXED')).toUpperCase();

 const stepMinor = pickFirstDefined(
   priceObj.increment,
   priceObj.step,
   product.price?.increment,
   product.raw?.price?.increment,
   product.price?.step,
   product.raw?.price?.step
 );
 const priceStep =
   Number.isFinite(toNum(stepMinor)) && Number.isFinite(divisor) && divisor > 0
     ? toNum(stepMinor) / divisor
     : 1;


// normalize requiredFields to ['recipient.email', ...]
const requiredFields = Array.isArray(product.fullDetail?.requiredFields)
  ? product.fullDetail.requiredFields
  : (Array.isArray(product.requiredFields) ? product.requiredFields : []);
const requiredFieldsNorm = requiredFields
  .map(f => (typeof f === 'string' ? f : f?.key))
  .filter(Boolean);

// Ensure priceUSD (single) for PI
let priceUSD = Number.isFinite(product.priceUSD) ? product.priceUSD : null;
if (!Number.isFinite(priceUSD) && Number.isFinite(priceValue)) {
  priceUSD = (priceCurrency === 'USD')
    ? priceValue
    : convert(priceValue, priceCurrency, 'USD', opts.fx);

}// USD range
const priceMinUSD = Number.isFinite(priceMinValue)
  ? (priceCurrency === 'USD' ? priceMinValue : convert(priceMinValue, priceCurrency, 'USD', opts.fx))
  : null;
const priceMaxUSD = Number.isFinite(priceMaxValue)
  ? (priceCurrency === 'USD' ? priceMaxValue : convert(priceMaxValue, priceCurrency, 'USD', opts.fx))
  : null;

  // Optional local currency display
const userCurrency = (opts.userCurrency || user?.currency || 'USD').toUpperCase();

const priceLocal = Number.isFinite(priceValue)
  ? (userCurrency === priceCurrency ? priceValue : convert(priceValue, priceCurrency, userCurrency, opts.fx))
  : null;
const priceMinLocal = Number.isFinite(priceMinValue)
  ? (userCurrency === priceCurrency ? priceMinValue : convert(priceMinValue, priceCurrency, userCurrency, opts.fx))
  : null;
const priceMaxLocal = Number.isFinite(priceMaxValue)
  ? (userCurrency === priceCurrency ? priceMaxValue : convert(priceMaxValue, priceCurrency, userCurrency, opts.fx))
  : null;

// Displays
const fmt = (v, c) => Number.isFinite(v)
  ? new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(v)
  : null;

const priceDisplay       = fmt(priceValue,      priceCurrency);
const priceUSDDisplay    = fmt(priceUSD,        'USD');
const priceLocalDisplay  = fmt(priceLocal,      userCurrency);

const priceMinDisplay    = fmt(priceMinValue,   priceCurrency);
const priceMaxDisplay    = fmt(priceMaxValue,   priceCurrency);
const priceMinUSDDisplay = fmt(priceMinUSD,     'USD');
const priceMaxUSDDisplay = fmt(priceMaxUSD,     'USD');
const priceMinLocalDisp  = fmt(priceMinLocal,   userCurrency);
const priceMaxLocalDisp  = fmt(priceMaxLocal,   userCurrency);


  // ---- “recipient gets” for topups (unchanged) ----
  const sendNestedDiv = product.send?.currencyDivisor ?? product.send?.currency_divisor;
  const recipientGetsValue =
    (product.send && typeof product.send === 'object' && product.send.fixed != null && sendNestedDiv != null)
      ? Number(product.send.fixed) / Number(sendNestedDiv)
      : (product.send != null && product.sendCurrencyDivisor != null)
        ? Number(product.send) / Number(product.sendCurrencyDivisor)
        : null;

  const recipientGets = Number.isFinite(recipientGetsValue) ? recipientGetsValue.toFixed(2) : null;
  const sendCurrency  = product.send?.currency || product.sendCurrency || null;

    // ---- Face value (local destination currency) for vouchers/eSIMs/topups ----
  // ---- Face value (local destination currency) for vouchers/eSIMs/topups ----
  // Look for send in multiple places: top-level, fullDetail, raw, and a couple common aliases.
// ---- Face value (local destination currency) for vouchers/eSIMs/topups ----
// Always derive from `send.*` only — ignore `price.*` for face value
// Always derive from `send.*` only — ignore `price.*` for face value
const sendObj =
  (product.send && typeof product.send === 'object') ? product.send
  : (product.fullDetail && typeof product.fullDetail.send === 'object') ? product.fullDetail.send
  : (product.raw && typeof product.raw.send === 'object') ? product.raw.send
  : null;

const sendDiv = Number(sendObj?.currencyDivisor) || 100;
const sendCur = (sendObj?.currency || product.sendCurrency || 'USD').toUpperCase();

// ✅ include fixed
const sendFixed = toNum(sendObj?.fixed ?? sendObj?.suggestedFixed);
const sendMin = toNum(sendObj?.min ?? sendObj?.minimum);
const sendMax = toNum(sendObj?.max ?? sendObj?.maximum);

const sendStep = toNum(
  sendObj?.increment ?? sendObj?.step ?? sendObj?.stepSize ?? sendObj?.denominationStep
);

// decide denomination type
const denominationType =
  Number.isFinite(sendFixed) ? 'FIXED'
  : (Number.isFinite(sendMin) && Number.isFinite(sendMax) && sendMin === sendMax && !sendStep) ? 'FIXED'
  : (Number.isFinite(sendMin) || Number.isFinite(sendMax) || Number.isFinite(sendStep)) ? 'RANGE'
  : 'FIXED';

// ✅ pick face minor properly
let faceMinor = null;
if (denominationType === 'FIXED') {
  faceMinor =
    Number.isFinite(sendFixed) ? sendFixed
    : Number.isFinite(sendMin) ? sendMin
    : Number.isFinite(sendMax) ? sendMax
    : null;
} else {
  // RANGE: use min as “starting at”
  faceMinor = Number.isFinite(sendMin) ? sendMin : null;
}

const faceValue = Number.isFinite(faceMinor) ? faceMinor / sendDiv : null;
const faceCurrency = sendCur;
const faceDisplay =
  Number.isFinite(faceValue)
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: faceCurrency }).format(faceValue)
    : null;

const hasLocalFace = Boolean(faceDisplay);
// one UI-friendly field: "local value" (destination currency)
const localValueDisplay =
  faceDisplay
    ? faceDisplay
    : (Number.isFinite(toNum(recipientGetsValue)) && sendCurrency)
      ? new Intl.NumberFormat(undefined, { style: 'currency', currency: String(sendCurrency).toUpperCase() })
          .format(recipientGetsValue)
      : null;

const localCurrency =
  faceCurrency || (sendCurrency ? String(sendCurrency).toUpperCase() : null);

  return {
    ...product,
    productType: type,
    topupSubtype,
    subTypes,
    name: product.brandName || product.name || 'Unnamed Product',
    image: product.image || product.brandLogo || null,
    details:
  product.details ??
  product.fullDetail?.details ??
  (details.length ? details : null),
hasRange,
priceMinValue: Number.isFinite(priceMinValue) ? priceMinValue : null,
priceMaxValue: Number.isFinite(priceMaxValue) ? priceMaxValue : null,
priceMinDisplay,
priceMaxDisplay,
  priceType,          // 'RANGE' | 'FIXED'
  priceStep,          // major-unit increment (default 1)
  requiredFields: requiredFieldsNorm,
priceMinUSD: Number.isFinite(priceMinUSD) ? priceMinUSD : null,
priceMaxUSD: Number.isFinite(priceMaxUSD) ? priceMaxUSD : null,
priceMinUSDDisplay,
priceMaxUSDDisplay,
  localValueDisplay,
  localCurrency,
priceMinLocal: Number.isFinite(priceMinLocal) ? priceMinLocal : null,
priceMaxLocal: Number.isFinite(priceMaxLocal) ? priceMaxLocal : null,
priceMinLocalDisplay: priceMinLocalDisp,
priceMaxLocalDisplay: priceMaxLocalDisp,

    // ✅ unified price outputs
    priceValue: Number.isFinite(priceValue) ? priceValue : null,       // numeric, native (USD for your eSIMs)
    priceDisplay,                                                      // e.g. "$40.00"
    priceUSD: Number.isFinite(priceUSD) ? priceUSD : null,             // numeric USD (for PI)
    priceUSDDisplay,                                                   // e.g. "$40.00"
    priceCurrency,                                                     // "USD"
    priceLocal: Number.isFinite(priceLocal) ? priceLocal : null,       // numeric in user currency (optional)
    priceLocalDisplay,                                                 // e.g. "€36.80" if FX provided
    userCurrency,                                                      // echoed back for UI
    // ✅ local face value (for vouchers/eSIMs; also fine for topups)
   faceValue,                 // numeric destination amount
    faceCurrency,              // e.g., "GTQ"
    faceDisplay,               // e.g., "Q3,600.00"
    hasLocalFace,              // boolean for conditional UI

    recipientGets,
    sendCurrency,

    
    recipient: user?.email ? { email: user.email } : undefined

  };
}
